import { useState, useEffect, useMemo } from "react";
import styles from "./FareOptions.module.css";
import { Search, Download, RefreshCw, Edit2, Trash2, CheckCircle, XCircle } from "lucide-react";
import AddFareOptionModal from "../../../../components/Products/Flights/FareOptions/AddFareOptionModal";
import {
  useGetFareOptions,
  useCreateFareOption,
  useUpdateFareOption,
  useDeleteFareOption,
} from "../../../../api/products/useFareOptionsApi";
import { useGetFareClasses } from "../../../../api/products/useFareClassesApi";

// Helper to render a baggage cell compactly
const BaggageCell = ({ included, pieces, weight, size, noneLabel = "Not Included" }) => {
  if (!included || pieces == null || pieces === 0) {
    return (
      <div className={styles.baggageStatus}>
        <XCircle size={16} className={styles.xIcon} />
        <span className={styles.notIncludedText}>{noneLabel}</span>
      </div>
    );
  }
  return (
    <div className={styles.baggageDetail}>
      <div className={styles.baggageDetailMain}>
        <CheckCircle size={15} className={styles.checkIcon} />
        <span className={styles.baggageSummary}>
          {pieces} × {weight}kg
        </span>
      </div>
      {size && <span className={styles.sizeBadge}>{size}</span>}
    </div>
  );
};

// Group fare options by fareClassId for display
function groupFareOptionsByFareClass(fareOptions) {
  const byFareClass = {};
  for (const fo of fareOptions) {
    const key = fo.fareClassId;
    if (!byFareClass[key]) {
      byFareClass[key] = {
        fareClassId: fo.fareClassId,
        fareClassCode: fo.fareClassCode,
        fareClassName: fo.fareClassName,
        airlineName: fo.airlineName,
        cabinClassName: fo.cabinClassName,
        handbag: null,
        cabinBaggage: null,
        checkedBaggage: null,
        ids: [],
      };
    }
    const row = byFareClass[key];
    row.ids.push(fo.id);
    if (fo.baggageType === "Handbag") row.handbag = fo;
    else if (fo.baggageType === "Cabin Baggage") row.cabinBaggage = fo;
    else if (fo.baggageType === "Checked Baggage") row.checkedBaggage = fo;
  }
  return Object.values(byFareClass);
}

const FareOptions = () => {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [fareClassFilter, setFareClassFilter] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: fareOptions = [], loading, error, refetch } = useGetFareOptions({
    fareClassId: fareClassFilter === "all" ? "" : fareClassFilter,
  });

  const { data: fareClasses = [] } = useGetFareClasses({ page: 1, limit: 500 });

  const { execute: createFareOption } = useCreateFareOption();
  const { execute: updateFareOption } = useUpdateFareOption();
  const { execute: deleteFareOption } = useDeleteFareOption();

  const grouped = useMemo(() => groupFareOptionsByFareClass(fareOptions), [fareOptions]);

  const filteredGroups = useMemo(() => {
    if (!debouncedSearch) return grouped;
    const q = debouncedSearch.toLowerCase();
    return grouped.filter(
      (g) =>
        (g.fareClassName || "").toLowerCase().includes(q) ||
        (g.fareClassCode || "").toLowerCase().includes(q) ||
        (g.airlineName || "").toLowerCase().includes(q)
    );
  }, [grouped, debouncedSearch]);

  const handleEdit = (group) => {
    setEditingGroup(group);
    setApiError(null);
    setShowAddModal(true);
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Delete all baggage options for ${group.fareClassName} (${group.fareClassCode})?`)) return;
    let failed = false;
    for (const id of group.ids) {
      const result = await deleteFareOption(id);
      if (!result.success) {
        alert(result.message || "Failed to delete fare option");
        failed = true;
        break;
      }
    }
    if (!failed) refetch();
  };

  const handleSaveFareOption = async (payload) => {
    setApiError(null);
    if (editingGroup) {
      // Delete baggage types that were removed (had data, now unchecked)
      const toDelete = [];
      if (editingGroup.handbag && !payload.handbag) toDelete.push(deleteFareOption(editingGroup.handbag.id));
      if (editingGroup.cabinBaggage && !payload.cabinBaggage) toDelete.push(deleteFareOption(editingGroup.cabinBaggage.id));
      if (editingGroup.checkedBaggage && !payload.checkedBaggage) toDelete.push(deleteFareOption(editingGroup.checkedBaggage.id));
      if (toDelete.length) {
        const delResults = await Promise.all(toDelete);
        const delFailed = delResults.find((r) => r && !r.success);
        if (delFailed) {
          setApiError(delFailed.message);
          return delFailed.message;
        }
      }
      // Update existing
      const updates = [];
      if (payload.handbag && editingGroup.handbag) updates.push(updateFareOption(editingGroup.handbag.id, payload.handbag));
      if (payload.cabinBaggage && editingGroup.cabinBaggage) updates.push(updateFareOption(editingGroup.cabinBaggage.id, payload.cabinBaggage));
      if (payload.checkedBaggage && editingGroup.checkedBaggage) updates.push(updateFareOption(editingGroup.checkedBaggage.id, payload.checkedBaggage));
      if (updates.length) {
        const updateResults = await Promise.all(updates);
        const updateFailed = updateResults.find((r) => r && !r.success);
        if (updateFailed) {
          setApiError(updateFailed.message);
          return updateFailed.message;
        }
      }
      // Create new baggage types that didn't exist
      const creates = [];
      if (payload.handbag && !editingGroup.handbag) creates.push(createFareOption({ ...payload.handbag, fareClassId: editingGroup.fareClassId }));
      if (payload.cabinBaggage && !editingGroup.cabinBaggage) creates.push(createFareOption({ ...payload.cabinBaggage, fareClassId: editingGroup.fareClassId }));
      if (payload.checkedBaggage && !editingGroup.checkedBaggage) creates.push(createFareOption({ ...payload.checkedBaggage, fareClassId: editingGroup.fareClassId }));
      if (creates.length) {
        const createResults = await Promise.all(creates);
        const createFailed = createResults.find((r) => r && !r.success);
        if (createFailed) {
          setApiError(createFailed.message);
          return createFailed.message;
        }
      }
      refetch();
      handleCloseModal();
      return;
    }
    // Create new: payload has { fareClassId, handbag, cabinBaggage, checkedBaggage }
    const alreadyHasOptions = fareOptions.some((fo) => Number(fo.fareClassId) === Number(payload.fareClassId));
    if (alreadyHasOptions) {
      setApiError("This fare class already has baggage options. Use Edit instead.");
      return "This fare class already has baggage options. Use Edit instead.";
    }
    const creates = [];
    if (payload.handbag) creates.push(createFareOption({ ...payload.handbag, fareClassId: payload.fareClassId }));
    if (payload.cabinBaggage) creates.push(createFareOption({ ...payload.cabinBaggage, fareClassId: payload.fareClassId }));
    if (payload.checkedBaggage) creates.push(createFareOption({ ...payload.checkedBaggage, fareClassId: payload.fareClassId }));
    if (creates.length === 0) {
      setApiError("At least one baggage type must be configured");
      return "At least one baggage type must be configured";
    }
    const results = await Promise.all(creates);
    const failed = results.find((r) => r && !r.success);
    if (failed) {
      setApiError(failed.message);
      return failed.message;
    }
    refetch();
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingGroup(null);
    setApiError(null);
  };

  const handleRefresh = () => refetch();
  const handleExport = () => console.log("EXPORT FARE OPTIONS DATA");

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1>Fare Options</h1>
          <p className={styles.subtitle}>
            Define baggage allowances and inclusions for each fare class. Fixed per fare class and not editable per booking.
          </p>
        </div>
        <button className={styles.addBtn} onClick={() => setShowAddModal(true)}>
          + Add Fare Option
        </button>
      </div>

      <div className={styles.infoBanner}>
        <div className={styles.infoIcon}>ℹ️</div>
        <div className={styles.infoText}>
          <strong>Scope Notice:</strong> Fare options currently cover baggage allowances only (handbag, cabin baggage, and checked baggage). These settings are fixed per fare class and cannot be modified at booking level.
        </div>
      </div>

      {error && (
        <div className={styles.infoBanner} style={{ background: "#fef2f2", borderColor: "#fecaca" }}>
          <div className={styles.infoIcon}>⚠️</div>
          <div className={styles.infoText}>
            {error}
            <button className={styles.retryBtn} onClick={() => refetch()}>Retry</button>
          </div>
        </div>
      )}

      <div className={styles.controls}>
        <div className={styles.searchBox}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by fare class, code, or airline..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div className={styles.filters}>
          <select
            value={fareClassFilter}
            onChange={(e) => setFareClassFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Fare Classes</option>
            {fareClasses.map((fc) => (
              <option key={fc.id} value={fc.id}>
                {fc.fareClassName} ({fc.fareClassCode}) - {fc.airline}
              </option>
            ))}
          </select>
          <button className={styles.iconBtn} onClick={handleRefresh} title="Refresh">
            <RefreshCw size={18} />
          </button>
          <button className={styles.iconBtn} onClick={handleExport} title="Export">
            <Download size={18} />
          </button>
        </div>
      </div>

      <div className={styles.resultsCount}>
        Showing {filteredGroups.length} of {grouped.length} fare classes
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>FARE CLASS</th>
              <th>👜 HANDBAG</th>
              <th>🎒 CABIN BAGGAGE</th>
              <th>🧳 CHECKED BAGGAGE</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className={styles.noData}>
                  <div className={styles.noDataContent}>
                    <p>Loading fare options…</p>
                  </div>
                </td>
              </tr>
            ) : filteredGroups.length === 0 ? (
              <tr>
                <td colSpan="5" className={styles.noData}>
                  <div className={styles.noDataContent}>
                    <div className={styles.noDataIcon}>💼</div>
                    <p>No fare options found</p>
                    <span>Try adjusting your search or add baggage options for a fare class</span>
                  </div>
                </td>
              </tr>
            ) : (
              filteredGroups.map((group) => (
                <tr key={group.fareClassId}>
                  <td>
                    <div className={styles.fareClassCell}>
                      <div className={styles.fareCodeBadge}>{group.fareClassCode || "—"}</div>
                      <div className={styles.fareClassInfo}>
                        <strong>{group.fareClassName || "—"}</strong>
                        <span className={styles.airlineLabel}>{group.airlineName || "—"}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <BaggageCell
                      included={group.handbag?.included}
                      pieces={group.handbag?.numberOfPieces}
                      weight={group.handbag?.weightPerPiece}
                      size={group.handbag?.size}
                    />
                  </td>
                  <td>
                    <BaggageCell
                      included={group.cabinBaggage?.included}
                      pieces={group.cabinBaggage?.numberOfPieces}
                      weight={group.cabinBaggage?.weightPerPiece}
                      size={group.cabinBaggage?.size}
                    />
                  </td>
                  <td>
                    {!group.checkedBaggage?.included || group.checkedBaggage?.numberOfPieces === 0 ? (
                      <div className={styles.baggageStatus}>
                        <XCircle size={16} className={styles.xIcon} />
                        <span className={styles.notIncludedText}>No baggage</span>
                      </div>
                    ) : (
                      <div className={styles.baggageDetail}>
                        <div className={styles.baggageDetailMain}>
                          <CheckCircle size={15} className={styles.checkIcon} />
                          <span className={styles.baggageSummary}>
                            {group.checkedBaggage.numberOfPieces} × {group.checkedBaggage.weightPerPiece}kg
                          </span>
                        </div>
                        {group.checkedBaggage.size && (
                          <span className={styles.sizeBadge}>{group.checkedBaggage.size}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button className={styles.editBtn} onClick={() => handleEdit(group)} title="Edit">
                        <Edit2 size={16} />
                      </button>
                      <button className={styles.deleteBtn} onClick={() => handleDelete(group)} title="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AddFareOptionModal
        open={showAddModal}
        onClose={handleCloseModal}
        onSave={handleSaveFareOption}
        fareOptionGroup={editingGroup}
        fareClasses={fareClasses}
        apiError={apiError}
      />
    </div>
  );
};

export default FareOptions;
